import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Badge, Box, Checkbox, Group, Text, TextInput, UnstyledButton } from '@mantine/core'

// #1080 検索サイドバーの複数選択ポップアップ(エリア/ポジション/スキル/担当工程で共用)。
// インクリメンタルサーチで絞り込み、選択済みは検索欄の上にタグとして表示、
// 「確定」ボタンでポップアップを閉じる。フォーム送信はhidden inputで既存のRailsフォームに任せる。
//
// ポップアップはportalで表示する。「条件検索」等の折りたたみ(.advanced-search-collapse)は
// overflow:hiddenかつトグル開閉時にのみ再計算されるmax-heightを持つため、DOM上の親のまま
// 重ねるとポップアップの下側(選択肢が多い場合の確定ボタンなど)が切れてしまう。
// また、幅狭時は<dialog>のshowModal()でトップレイヤーに昇格しているため、
// document.bodyへportalするとz-indexに関わらずモーダルの後ろに隠れてしまう。
// 開いている<dialog>祖先があればその中へ、なければdocument.bodyへportalする。
export default function MultiSelectPopupField({ fieldName, placeholder, options = [], initialValues = [] }) {
  const [opened, setOpened] = useState(false)
  const [selected, setSelected] = useState(() => new Set(initialValues.map(String)))
  const [query, setQuery] = useState('')
  const triggerRef = useRef(null)
  const [popupStyle, setPopupStyle] = useState(null)
  const [portalTarget, setPortalTarget] = useState(null)

  useLayoutEffect(() => {
    if (!opened || !triggerRef.current) return

    const dialog = triggerRef.current.closest('dialog')
    setPortalTarget(dialog && dialog.open ? dialog : document.body)

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect()
      const viewportH = window.innerHeight
      const spaceBelow = viewportH - rect.bottom - 12
      const spaceAbove = rect.top - 12
      const preferredHeight = 420
      const base = { position: 'fixed', left: rect.left, width: Math.max(rect.width, 320) }

      // 下に十分な余白が無く、上の方が広ければ上向きに開く(フリップ)
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setPopupStyle({ ...base, bottom: viewportH - rect.top + 4, maxHeight: Math.min(preferredHeight, spaceAbove) })
      } else {
        setPopupStyle({ ...base, top: rect.bottom + 4, maxHeight: Math.min(preferredHeight, spaceBelow) })
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [opened])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  const selectedOptions = options.filter((o) => selected.has(o.value))

  const toggle = (value) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const popup = opened && popupStyle && portalTarget && createPortal(
    <>
      <Box onClick={() => setOpened(false)} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
      <Box
        style={{
          ...popupStyle,
          maxWidth: '90vw',
          background: '#fff',
          border: '1px solid var(--mantine-color-gray-3, #e1e6ee)',
          borderRadius: 'var(--mantine-radius-md, 8px)',
          boxShadow: 'var(--mantine-shadow-md, 0 4px 12px rgba(0,0,0,0.12))',
          zIndex: 300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box p="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-2, #e9ecef)', flexShrink: 0 }}>
          <TextInput
            placeholder={`${placeholder}を検索`}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            size="sm"
            autoFocus
          />
          {selectedOptions.length > 0 && (
            <Box mt={8} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selectedOptions.map((o) => (
                <Badge
                  key={o.value}
                  color="brand"
                  variant="light"
                  tt="none"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggle(o.value)}
                  rightSection={<span aria-hidden="true">×</span>}
                >
                  {o.label}
                </Badge>
              ))}
            </Box>
          )}
        </Box>

        <Box p="xs" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {filtered.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="md">該当する項目がありません</Text>
          )}
          {filtered.map((o, i) => {
            // options に group がある場合(スキルのカテゴリ等)は、グループが変わる位置に見出しを挟む
            const showGroup = o.group && (i === 0 || filtered[i - 1].group !== o.group)
            return (
              <Box key={o.value}>
                {showGroup && (
                  <Text size="xs" fw={700} c="dimmed" mt={i === 0 ? 2 : 10} mb={2} px={4} style={{ letterSpacing: '0.04em' }}>
                    {o.group}
                  </Text>
                )}
                <Checkbox
                  checked={selected.has(o.value)}
                  onChange={() => toggle(o.value)}
                  label={o.label}
                  size="sm"
                  py={4}
                  styles={{ body: { alignItems: 'center' }, labelWrapper: { alignItems: 'center' } }}
                />
              </Box>
            )
          })}
        </Box>

        <Box p="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2, #e9ecef)', flexShrink: 0 }}>
          <UnstyledButton
            onClick={() => setOpened(false)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'center',
              padding: '8px',
              background: 'var(--mantine-color-brand-6, #4a71ac)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 'var(--mantine-radius-sm, 4px)',
            }}
          >
            確定
          </UnstyledButton>
        </Box>
      </Box>
    </>,
    portalTarget
  )

  return (
    <Box>
      <input type="hidden" name={fieldName} value="" />
      {[...selected].map((v) => (
        <input key={v} type="hidden" name={fieldName} value={v} />
      ))}

      <Group gap={6} align="center" wrap="wrap" className="search-field-row">
        <span className="search-field-label">{placeholder}：</span>

        {selectedOptions.map((o) => (
          <Badge
            key={o.value}
            color="brand"
            variant="light"
            tt="none"
            style={{ cursor: 'pointer' }}
            onClick={() => toggle(o.value)}
            rightSection={<span aria-hidden="true">×</span>}
          >
            {o.label}
          </Badge>
        ))}

        <UnstyledButton
          ref={triggerRef}
          onClick={() => setOpened((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            height: 26,
            padding: '0 10px',
            border: '1px dashed var(--mantine-color-gray-4, #ced4da)',
            borderRadius: 'var(--mantine-radius-xl, 999px)',
            background: '#fff',
            color: 'var(--mantine-color-brand-7, #4a71ac)',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          + 追加
        </UnstyledButton>
      </Group>

      {popup}
    </Box>
  )
}
